import { useRef, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';

/**
 * Renders a multi-select list of options for a follow-up question, including an "other" option with a text input.
 * @param {Object} question - The question object containing options to display.
 * @param {string[]} selectedOptions - Array of currently selected option IDs.
 * @param {Function} onOptionClick - Callback when an option is toggled.
 * @param {string} otherText - Current text value for the "other" option input.
 * @param {boolean} disabled - Whether the options should be non-interactive (e.g. while submitting).
 * @param {Function} onOtherTextChange - Change handler for the "other" text input.
 */
export const OptionsList = ({
  question,
  selectedOptions,
  onOptionClick,
  otherText,
  disabled,
  onOtherTextChange,
}) => {
  const otherInputRef = useRef(null);
  const isOtherSelected = selectedOptions.includes('other');

  // Focus the "other" input when it becomes visible
  useEffect(() => {
    if (isOtherSelected && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  }, [isOtherSelected]);

  if (!question.options) return null;

  return (
    <div className='space-y-3 mb-8'>
      {question.options.map((option) => {
        const isSelected = selectedOptions.includes(option.id);
        const isOtherOption = option.id === 'other';

        // "Other" option with text input inside
        if (isOtherOption) {
          return (
            <div
              key={`${question.id}-${option.id}`}
              onClick={() => {
                if (disabled) return;
                // Once selected this row is a text field, not a toggle: clicking it focuses the
                // input (so the text cursor it shows is honest). Deselecting is the check's job.
                if (isSelected) otherInputRef.current?.focus();
                else onOptionClick(option.id);
              }}
              // Selected, this row reads as a text field: a text cursor and NO hover from the
              // text side. Hover is triggered only by the check area (which is the only part that
              // deselects) but fills the whole row, via :has() on the child's hover state.
              className={`w-full h-[60px] flex items-center justify-between px-5 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? `border-emerald-500 bg-white ${
                      disabled ? '' : 'has-[[data-deselect]:hover]:bg-gray-100'
                    }`
                  : `border-gray-200 bg-white ${disabled ? '' : 'hover:border-gray-300 hover:bg-gray-100'}`
              } ${disabled ? 'cursor-not-allowed' : isSelected ? 'cursor-text' : 'cursor-pointer'}`}>
              {isSelected ? (
                <input
                  ref={otherInputRef}
                  type='text'
                  disabled={disabled}
                  value={otherText}
                  onChange={onOtherTextChange}
                  onClick={(e) => e.stopPropagation()}
                  placeholder='Please specify...'
                  className='flex-1 p-0 bg-transparent border-0 outline-none focus:outline-none focus:ring-0 text-gray-800 placeholder:text-gray-400 placeholder:italic'
                  style={{ boxShadow: 'none' }}
                />
              ) : (
                <span className='text-gray-400 italic'>{option.label}</span>
              )}

              {isSelected && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (disabled) return;
                    onOptionClick(option.id);
                  }}
                  data-deselect
                  // Deselect hit area. py-3/pl-3/pr-5 grow it from the bare 20x20 icon to 44x52,
                  // reaching the row's inner edge so the entire right-hand side deselects, while
                  // -my-3/-mr-5 cancel that back out of the layout — the box still occupies the
                  // 20x32 the icon alone did, so nothing reflows and the check does not move.
                  // The hover fill lives on the row (see has-[[data-deselect]:hover] above), not
                  // here, so pointing at this area tints the whole row.
                  className={`-my-3 -mr-5 py-3 pl-3 pr-5 flex-shrink-0 ${
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  }`}>
                  <CheckIcon className='w-5 h-5 text-emerald-500' />
                </div>
              )}
            </div>
          );
        }

        // Regular option
        return (
          <button
            key={`${question.id}-${option.id}`}
            onClick={() => onOptionClick(option.id)}
            disabled={disabled}
            className={`w-full h-[60px] flex items-center justify-between px-5 rounded-xl border-2 transition-all text-left ${
              isSelected
                ? `border-emerald-500 bg-white ${disabled ? '' : 'hover:bg-gray-100'}`
                : `border-gray-200 bg-white ${disabled ? '' : 'hover:border-gray-300 hover:bg-gray-100'}`
            } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            <span className='text-gray-800'>{option.label}</span>
            {isSelected && <CheckIcon className='w-5 h-5 text-emerald-500 flex-shrink-0' />}
          </button>
        );
      })}
    </div>
  );
};
